class WebSocketService {
  constructor() {
    this.socket = null
    this.store = null
    this.stationSlug = null
    this.reconnectDelay = 1000
    this.maxReconnectDelay = 30000
    this.reconnectTimer = null
    this.listeners = new Map() // Event listeners for custom handlers
    this.isDestroyed = false
  }

  connect(store, stationSlug = null) {
    this.store = store
    this.isDestroyed = false
    
    // Cancel any pending reconnect
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    // If already connected to the same station, don't reconnect
    if (this.socket && 
        this.socket.readyState === WebSocket.OPEN && 
        this.stationSlug === stationSlug) {
      return
    }
    
    // Close existing socket if any (without triggering reconnect)
    if (this.socket) {
      // Remove handlers to prevent onclose from triggering reconnect
      this.socket.onclose = null
      this.socket.onerror = null
      this.socket.onmessage = null
      if (this.socket.readyState === WebSocket.OPEN || 
          this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close()
      }
      this.socket = null
    }
    
    this.stationSlug = stationSlug
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // If station slug provided, connect to station-specific WS
    const wsPath = stationSlug ? `/ws/${stationSlug}` : '/ws'
    const wsUrl = `${protocol}//${window.location.host}${wsPath}`
    
    try {
      this.socket = new WebSocket(wsUrl)
      
      this.socket.onopen = () => {

        if (this.store) this.store.commit('SET_WS_CONNECTED', true)
        this.reconnectDelay = 1000
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer)
          this.reconnectTimer = null
        }
      }
      
      this.socket.onclose = () => {
        if (this.store) this.store.commit('SET_WS_CONNECTED', false)
        // Only reconnect if not intentionally destroyed
        if (!this.isDestroyed) {
          this.scheduleReconnect()
        }
      }
      
      this.socket.onerror = (err) => console.error('[WS] Error:', err)
      this.socket.onmessage = (event) => this.handleMessage(event.data)
    } catch (err) {
      console.error('[WS] Connection error:', err)
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer || this.isDestroyed) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay)
      if (this.store && !this.isDestroyed) this.connect(this.store, this.stationSlug)
    }, this.reconnectDelay)
  }

  // Subscribe to a specific event type
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }
    const callbacks = this.listeners.get(eventType)
    
    // Check if this exact callback is already registered
    if (callbacks.has(callback)) {
      console.warn(`[WS] Duplicate listener for ${eventType}, skipping`)
      return () => {} // Return no-op unsubscribe
    }
    
    callbacks.add(callback)
    
    // Return unsubscribe function
    return () => {
      callbacks.delete(callback)
    }
  }

  // Emit to all listeners for an event type
  emit(eventType, data) {
    const callbacks = this.listeners.get(eventType)
    if (callbacks) {
      callbacks.forEach(cb => {
        try {
          cb(data)
        } catch (err) {
          console.error('[WS] Listener error:', err)
        }
      })
    }
  }

  handleMessage(data) {
    try {
      const msg = JSON.parse(data)
      
      // Verify message is for current station (if stationId is in message)
      if (msg.stationId && this.store && this.store.state.station) {
        if (msg.stationId !== this.store.state.station.id) {
          return
        }
      }
      
      // Emit to custom listeners first
      this.emit(msg.type, msg)
      
      // Then handle store updates
      switch (msg.type) {
        case 'connected':
          if (this.store) this.store.commit('SET_SOURCE_CONNECTED', msg.sourceConnected)
          break
        case 'source-connected':
          if (this.store) this.store.commit('SET_SOURCE_CONNECTED', true)
          break
        case 'source-disconnected':
          if (this.store) this.store.commit('SET_SOURCE_CONNECTED', false)
          break
        case 'metadata':
          if (this.store) this.store.commit('ADD_METADATA_ITEM', msg.data)
          break
        case 'segment':
          // New segment created - add directly to store
          if (this.store && msg.data) {
            this.store.commit('ADD_SEGMENT', msg.data)
          }
          break
        case 'segment-peaks':
          // Segment updated with peaks - update in store
          if (this.store && msg.data && msg.data.segmentId) {
            this.store.commit('UPDATE_SEGMENT', { 
              id: msg.data.segmentId, 
              updates: { peaks: msg.data.peaks } 
            })
          }
          break
        case 'station-status':
          // Station status update - emit for StationSelector
          // Already emitted above via this.emit()
          break
        case 'export-started':
        case 'export-progress':
        case 'export-complete':
        case 'export-failed':
          // Export events - already emitted above via this.emit()
          break
      }
    } catch (err) {
      console.error('[WS] Parse error:', err)
    }
  }

  disconnect() {
    this.isDestroyed = true
    
    // Cancel any pending reconnect first
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    if (this.socket) {
      // Remove handlers to prevent onclose from triggering reconnect
      this.socket.onclose = null
      this.socket.onerror = null
      this.socket.onmessage = null
      this.socket.onopen = null
      
      if (this.socket.readyState === WebSocket.OPEN || 
          this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close()
      }
      this.socket = null
    }
    
    this.stationSlug = null
    // Clear all listeners to prevent memory leaks
    this.listeners.clear()
  }

  // Clear all listeners (call when completely shutting down)
  clearAllListeners() {
    this.listeners.clear()
  }

  // Check if connected to a specific station
  isConnectedTo(stationSlug) {
    return this.socket && 
           this.socket.readyState === WebSocket.OPEN && 
           this.stationSlug === stationSlug
  }

  // Get current connection state
  get connected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN
  }
}

export default new WebSocketService()
